// Navigation model: sidebar groups (Compliance and Grid code for every reader, with Assessments for Confidential users, Operations for operators, Administration for admins: Users, Feedback, Settings, Datasets and System) and items with icons, routes, required access levels (never role strings), the running-jobs, assessments-to-review and new-feedback badges and g-sequence hotkeys, plus role checks from /me.
import {
  ActivityIcon,
  BadgeCheckIcon,
  BookAIcon,
  ChartLineIcon,
  ClipboardCheckIcon,
  DatabaseIcon,
  HistoryIcon,
  InboxIcon,
  InfoIcon,
  LayoutDashboardIcon,
  LibraryIcon,
  ListChecksIcon,
  RadarIcon,
  ServerIcon,
  Settings2Icon,
  SlidersHorizontalIcon,
  SparklesIcon,
  UsersIcon,
  type LucideIcon,
} from "lucide-react";

import type { Role } from "@/api/__generated__";

export type AccessLevel = "R" | "C" | "O" | "A";

export const ROLE_FOR_LEVEL: Record<AccessLevel, Role> = {
  R: "reader",
  C: "confidential",
  O: "operator",
  A: "admin",
};

export function canAccess(roles: readonly Role[], level: AccessLevel): boolean {
  return roles.includes("admin") || roles.includes(ROLE_FOR_LEVEL[level]);
}

export type RegionPath =
  | "/$region"
  | "/$region/assessments"
  | "/$region/workspace"
  | "/$region/assistant"
  | "/$region/library"
  | "/$region/parameters"
  | "/$region/curves"
  | "/$region/glossary"
  | "/$region/quality"
  | "/$region/versions";

export type GlobalPath =
  | "/ops/runs"
  | "/ops/sources"
  | "/ops/system"
  | "/admin/users"
  | "/admin/feedback"
  | "/admin/settings"
  | "/admin/datasets"
  | "/about";

export type NavGroupId = "compliance" | "gridcode" | "operations" | "administration";
export type NavBadgeKind = "running-jobs" | "assessments-to-review" | "new-feedback";

interface NavItemBase {
  id: string;
  label: string;
  icon: LucideIcon;
  level: AccessLevel;
  hotkey?: string;
  badge?: NavBadgeKind;
  activePrefixes?: readonly string[];
}

export type NavItem = NavItemBase &
  ({ scope: "region"; to: RegionPath } | { scope: "global"; to: GlobalPath }) & {
    group: NavGroupId | "footer";
  };

export const NAV_GROUPS: readonly { id: NavGroupId; label: string }[] = [
  { id: "compliance", label: "Compliance" },
  { id: "gridcode", label: "Grid code" },
  { id: "operations", label: "Operations" },
  { id: "administration", label: "Administration" },
];

export const NAV_ITEMS: readonly NavItem[] = [
  {
    id: "overview",
    group: "compliance",
    label: "Overview",
    icon: LayoutDashboardIcon,
    scope: "region",
    to: "/$region",
    level: "R",
    hotkey: "o",
  },
  {
    id: "assessments",
    group: "compliance",
    label: "Assessments",
    icon: ClipboardCheckIcon,
    scope: "region",
    to: "/$region/assessments",
    level: "C",
    hotkey: "s",
    badge: "assessments-to-review",
  },
  {
    id: "assistant",
    group: "compliance",
    label: "Assistant",
    icon: SparklesIcon,
    scope: "region",
    to: "/$region/assistant",
    level: "R",
    hotkey: "a",
  },
  {
    id: "workspace",
    group: "gridcode",
    label: "Requirements",
    icon: ListChecksIcon,
    scope: "region",
    to: "/$region/workspace",
    level: "R",
    hotkey: "w",
  },
  {
    id: "library",
    group: "gridcode",
    label: "Library",
    icon: LibraryIcon,
    scope: "region",
    to: "/$region/library",
    level: "R",
    hotkey: "l",
  },
  {
    id: "curves",
    group: "gridcode",
    label: "Curves",
    icon: ChartLineIcon,
    scope: "region",
    to: "/$region/curves",
    level: "R",
    hotkey: "c",
  },
  {
    id: "glossary",
    group: "gridcode",
    label: "Glossary",
    icon: BookAIcon,
    scope: "region",
    to: "/$region/glossary",
    level: "R",
    hotkey: "y",
  },
  {
    id: "versions",
    group: "gridcode",
    label: "What changed",
    icon: HistoryIcon,
    scope: "region",
    to: "/$region/versions",
    level: "R",
    hotkey: "v",
  },
  {
    id: "quality",
    group: "operations",
    label: "Quality",
    icon: BadgeCheckIcon,
    scope: "region",
    to: "/$region/quality",
    level: "O",
    hotkey: "q",
  },
  {
    id: "parameters",
    group: "operations",
    label: "Parameters",
    icon: SlidersHorizontalIcon,
    scope: "region",
    to: "/$region/parameters",
    level: "O",
    hotkey: "p",
  },
  {
    id: "runs",
    group: "operations",
    label: "Runs & jobs",
    icon: ActivityIcon,
    scope: "global",
    to: "/ops/runs",
    level: "O",
    hotkey: "r",
    badge: "running-jobs",
    activePrefixes: ["/ops/runs", "/ops/jobs"],
  },
  {
    id: "sources",
    group: "operations",
    label: "Sources",
    icon: RadarIcon,
    scope: "global",
    to: "/ops/sources",
    level: "O",
  },
  {
    id: "users",
    group: "administration",
    label: "Users",
    icon: UsersIcon,
    scope: "global",
    to: "/admin/users",
    level: "A",
  },
  {
    id: "feedback",
    group: "administration",
    label: "Feedback",
    icon: InboxIcon,
    scope: "global",
    to: "/admin/feedback",
    level: "A",
    badge: "new-feedback",
  },
  {
    id: "settings",
    group: "administration",
    label: "Settings",
    icon: Settings2Icon,
    scope: "global",
    to: "/admin/settings",
    level: "A",
  },
  {
    id: "datasets",
    group: "administration",
    label: "Datasets",
    icon: DatabaseIcon,
    scope: "global",
    to: "/admin/datasets",
    level: "A",
    hotkey: "d",
    activePrefixes: ["/admin/datasets"],
  },
  {
    id: "system",
    group: "administration",
    label: "System",
    icon: ServerIcon,
    scope: "global",
    to: "/ops/system",
    level: "A",
  },
  { id: "about", group: "footer", label: "About", icon: InfoIcon, scope: "global", to: "/about", level: "R" },
];

export function visibleNavItems(roles: readonly Role[], items: readonly NavItem[] = NAV_ITEMS): NavItem[] {
  return items.filter((item) => canAccess(roles, item.level));
}

export function isNavItemActive(item: NavItem, pathname: string, regionPrefix: string | null): boolean {
  if (item.activePrefixes) {
    return item.activePrefixes.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
  }
  if (item.scope === "global") {
    return pathname === item.to || pathname.startsWith(`${item.to}/`);
  }
  if (regionPrefix === null) {
    return false;
  }
  const target = item.to.replace("/$region", regionPrefix);
  if (item.to === "/$region") {
    return pathname === target || pathname === `${target}/`;
  }
  return pathname === target || pathname.startsWith(`${target}/`);
}
